/**
 * Contact Us form and category value types. Kept separate from the generated GraphQL types so the
 * modal and its schema talk in form-shaped values (all strings + one boolean, as the controls
 * produce them) rather than the mutation's input shape.
 */

/** A selectable category, as the modal consumes it. */
export interface ContactUsCategory {
  /** Stable key submitted back to the mutation — never the label. */
  key: string;
  label: string;
  /** Category-specific middle line of the success confirmation. */
  successMiddleLine: string;
  /**
   * The legal text for the confirmation step. The backend populates it only for the cancellation
   * category, which is what tells the modal to insert that step at all — see
   * `isCancellationCategory`.
   */
  cancellationWarning: string | null;
  /**
   * Closing line of the success confirmation, after `successMiddleLine`. Separate from
   * `cancellationWarning` because they belong to different steps: the warning interrupts before
   * sending, this reassures after. Populated for the cancellation category only.
   */
  cancellationSuccessLine: string | null;
}

export interface ContactUsFormValues {
  categoryKey: string;
  name: string;
  email: string;
  callbackNumber: string;
  message: string;
  agreedToPhiDisclaimer: boolean;
}

/**
 * Whether picking this category routes through the cancellation confirmation step.
 *
 * Derived from the server-owned `cancellationWarning` rather than matching a hardcoded category
 * key: `cancellationWarning` is the only field in the `ContactUsCategory` contract that
 * distinguishes the cancellation category, and the backend decides which row carries it. Matching
 * a key string here would silently skip the legal confirmation if that key were ever renamed.
 */
export function isCancellationCategory(category: ContactUsCategory | undefined): boolean {
  return category?.cancellationWarning != null;
}
